import { Box, Slab } from '../../render/parts'
import { DESK_HEIGHT, Grommet, PanelLegs, Pedestal, TOP_THICKNESS } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Reception counter: a 0.74 work surface on the staff side (-Z) screened by a
 * 1.12 counter front. The front is a shell carcass wrapped in an accent fascia
 * that stands 10 mm proud, capped by a shell transaction top the visitor uses.
 * The `curvo` variant just feeds a large plan radius to every slab.
 */

const WIDTH = 2.2
const DEPTH = 0.95
const COUNTER_HEIGHT = 1.12
const FRONT_Z = DEPTH / 2

/** Counter mass, centred in Z between the transaction top's front edge and the work surface. */
const MASS_DEPTH = 0.4
const MASS_Z = FRONT_Z - 0.0375 - MASS_DEPTH / 2

const WORK_FRONT = 0.16
const WORK_DEPTH = WORK_FRONT + DEPTH / 2
const WORK_Z = WORK_FRONT - WORK_DEPTH / 2

function DeskReception({ accent, variant = 'recto' }: AssetContext) {
  const curved = variant === 'curvo'

  return (
    <group>
      {/* Recessed plinth so the counter mass reads as a lifted volume. */}
      <Slab
        tone="shellSoft"
        size={[2.06, MASS_DEPTH - 0.03, 0.07]}
        radius={curved ? 0.14 : 0.02}
        position={[0, 0.035, MASS_Z]}
      />
      <Slab
        tone="shell"
        size={[2.12, MASS_DEPTH, 1.04]}
        radius={curved ? 0.16 : 0.02}
        position={[0, 0.58, MASS_Z]}
      />
      {/* Accent fascia wrapping the mass, leaving a shell reveal above and below. */}
      <Slab
        tone="accent"
        color={accent}
        size={[2.14, MASS_DEPTH + 0.02, 0.9]}
        radius={curved ? 0.17 : 0.03}
        position={[0, 0.55, MASS_Z]}
      />
      {/* Transaction top: full width, flush with the front edge of the footprint. */}
      <Slab
        tone="shell"
        size={[WIDTH, 0.5, 0.045]}
        radius={curved ? 0.2 : 0.03}
        position={[0, COUNTER_HEIGHT - 0.0225, FRONT_Z - 0.25]}
      />

      {/* Staff side. */}
      <Slab
        tone="surface"
        size={[2.1, WORK_DEPTH, TOP_THICKNESS]}
        radius={curved ? 0.14 : 0.02}
        position={[0, DESK_HEIGHT - TOP_THICKNESS / 2, WORK_Z]}
      />
      <group position={[0, 0, WORK_Z]}>
        <PanelLegs width={2.1} depth={WORK_DEPTH} inset={0.14} />
      </group>
      <Box tone="shellSoft" size={[0.95, 0.024, 0.34]} position={[-0.42, 0.34, -0.2]} />
      <group position={[0.62, 0, -0.2]} rotation={[0, Math.PI, 0]}>
        <Pedestal width={0.42} depth={0.44} height={0.6} drawers={2} accent={accent} />
      </group>
      <Grommet x={-0.7} z={-0.1} r={0.03} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-reception',
  name: 'Mostrador de recepción',
  category: 'desks',
  footprint: [WIDTH, DEPTH],
  height: COUNTER_HEIGHT,
  surfaceHeight: DESK_HEIGHT,
  seats: 1,
  occupantSide: 'back',
  accentable: true,
  variants: [
    { id: 'recto', name: 'Recto' },
    { id: 'curvo', name: 'Curvo' },
  ],
  keywords: [
    'recepción',
    'mostrador',
    'counter',
    'reception desk',
    'front desk',
    'atención',
  ],
  Component: DeskReception,
}
